import React, { useState } from 'react';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Persona_Proceso } from 'data/crm/Personas';
import { Card, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

// Agregamos personaId para saber a quién vincular, y una función para recargar los datos
interface ProcesosProps {
  procesos: Persona_Proceso[];
  persona_id: string; 
  onProcesoAdded?: () => void; 
}

interface ProcesoOption {
  proc: string;
  AUX7: string;
}

interface RolOption {
  id: number;
  nombre: string;
}

const ProcesosEnPersonaDetails = ({ procesos, persona_id, onProcesoAdded }: ProcesosProps) => {
  const [showModal, setShowModal] = useState(false);
  const [procesosList, setProcesosList] = useState<ProcesoOption[]>([]);
  const [rolesList, setRolesList] = useState<RolOption[]>([]);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    proc: '',
    rol_id: ''
  });

  const handleClose = () => {
    setShowModal(false);
    setFormData({ proc: '', rol_id: '' }); // Limpiamos el form al cerrar
  };

  const handleShow = async () => {
    setShowModal(true);
    // Solo cargamos las dependencias si no las hemos cargado antes
    if (procesosList.length === 0) {
      setLoadingDeps(true);
      try {
        const response = await fetch('http://localhost:2000/personas/dependenciasformulario');
        if (!response.ok) throw new Error('Error al cargar dependencias');
        const data = await response.json();
        setProcesosList(data.procesos || []);
        setRolesList(data.roles || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoadingDeps(false);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('http://localhost:2000/personas/vincular-proceso', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          persona_id: persona_id,
          proc: formData.proc,
          rol_id: formData.rol_id
        }),
      });

      if (!response.ok) throw new Error('Error al vincular el proceso');

      alert('Proceso vinculado exitosamente');
      handleClose();
      
      // Si pasaste una función para recargar la página/datos desde el padre, la ejecutamos
      if (onProcesoAdded) {
        onProcesoAdded();
      } else {
        window.location.reload(); // Recarga simple como fallback
      }
      
    } catch (error) {
      console.error(error);
      alert('Hubo un error al vincular el proceso.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h4 className="mb-3"> Procesos ({procesos.length})</h4>

      {procesos.map((proceso) => (
        <Card key={proceso.id} className="mb-3 border-translucent">
          <Card.Body className="p-3">
            <Link
              to={`/app/apps/project-management/proceso-details/${proceso.proc}`} 
              className="text-decoration-none">
            <h5 className="mb-1 text-primary">{proceso.Proc.AUX7}</h5>
            </Link>
            <p className="fs-10 text-body-secondary mb-1">
              <strong>Rol:</strong> {proceso.rol_id} | <strong>Expediente:</strong> {proceso.Proc.EXP1} | <strong>Juzgado:</strong> {proceso.Proc.OJUD}
            </p>
          </Card.Body>
        </Card>
      ))}

      {procesos.length === 0 && (
        <p className="text-body-tertiary fs-9">No hay procesos registrados para esta persona.</p>
      )}

      <Button 
        startIcon={<FontAwesomeIcon icon={faPlus} />} 
        variant="link" 
        className="text-decoration-none p-0 mt-3"
        onClick={handleShow}
      >
        Agregar nuevo proceso
      </Button>

      {/* MODAL PARA AGREGAR PROCESO */}
      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Vincular a un Proceso</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {loadingDeps ? (
              <div className="d-flex justify-content-center p-4">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <Row className="g-3">
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Proceso <span className="text-danger">*</span></Form.Label>
                  <Form.Select required name="proc" value={formData.proc} onChange={handleChange}>
                    <option value="">Seleccione un proceso...</option>
                    {procesosList.map((p) => (
                      <option key={p.proc} value={p.proc}>{p.AUX7}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Rol en el Proceso <span className="text-danger">*</span></Form.Label>
                  <Form.Select required name="rol_id" value={formData.rol_id} onChange={handleChange} disabled={!formData.proc}>
                    <option value="">Seleccione un rol...</option>
                    {rolesList.map((r) => (
                      <option key={r.id} value={r.id}>{r.nombre}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting || !formData.proc || !formData.rol_id || loadingDeps}>
              {isSubmitting ? 'Guardando...' : 'Vincular Proceso'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </div>
  );
};

export default ProcesosEnPersonaDetails;